// src/components/LineToolbar.js
import { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardActionArea,
  CardHeader,
  TextField,
  InputAdornment,
  Stack,
} from "@mui/material";
import { Timeline as TimelineIcon } from "@mui/icons-material";
import { Line } from "fabric";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory";

const LineToolbar = ({ canvas }) => {
  const { selectedObject, setSelectedObject, canvasScale, setSnackbar } =
    useData();
  const { saveHistory } = useCanvasHistory(canvas);
  const [lineStroke, setLineStroke] = useState(1);

  // Sync lineStroke with selected line's strokeWidth
  useEffect(() => {
    if (selectedObject && selectedObject.type === "line") {
      setLineStroke(selectedObject.strokeWidth || 1);
      console.log("LineToolbar: Synced strokeWidth", {
        strokeWidth: selectedObject.strokeWidth,
      });
    }
  }, [selectedObject]);

  const addLine = useCallback(() => {
    if (!canvas || !canvasScale) {
      setSnackbar({
        open: true,
        message: "Canvas or scale not initialized.",
        severity: "error",
      });
      return;
    }

    console.log("LineToolbar: Checking fabric.Line", { Line: Line });
    if (!Line) {
      console.error("LineToolbar: fabric.Line is not defined");
      setSnackbar({
        open: true,
        message: "Failed to add line. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      const line = new Line(
        [
          -50,
          0, // Start point (relative to center)
          50,
          0, // End point (relative to center)
        ],
        {
          left: canvas.getWidth() / 2,
          top: canvas.getHeight() / 2,
          stroke: "#000000",
          strokeWidth: Math.max(1, parseInt(lineStroke) || 1),
          strokeUniform: true,
          originX: "center",
          originY: "center",
          preserveObjectStacking: false,
          type: "line",
        }
      );

      canvas.add(line);
      canvas.setActiveObject(line);
      setSelectedObject(line);
      canvas.requestRenderAll();
      saveHistory(canvas); // Save history for new line
      console.log("LineToolbar: Line added", { strokeWidth: lineStroke });
      setSnackbar({
        open: true,
        message: `Line with ${lineStroke}px stroke added.`,
        severity: "success",
      });
    } catch (error) {
      console.error("LineToolbar: Failed to add line", error);
      setSnackbar({
        open: true,
        message: "Error adding line. Please try again.",
        severity: "error",
      });
    }
  }, [
    canvas,
    canvasScale,
    lineStroke,
    setSelectedObject,
    setSnackbar,
    saveHistory,
  ]);

  const handleStrokeWidthChange = (e) => {
    const newStrokeWidth = Math.max(1, parseInt(e.target.value) || 1);
    setLineStroke(newStrokeWidth);

    if (selectedObject && selectedObject.type === "line") {
      try {
        selectedObject.set({ strokeWidth: newStrokeWidth });
        canvas.requestRenderAll();
        setSelectedObject(selectedObject); // Update context
        canvas.fire("object:modified", { target: selectedObject }); // Trigger event for history
        saveHistory(canvas); // Save history for stroke width change
        console.log("LineToolbar: Stroke width updated", {
          strokeWidth: newStrokeWidth,
        });
        setSnackbar({
          open: true,
          message: `Stroke width updated to ${newStrokeWidth}px.`,
          severity: "success",
        });
      } catch (error) {
        console.error("LineToolbar: Failed to update stroke width", error);
        setSnackbar({
          open: true,
          message: "Error updating stroke width. Please try again.",
          severity: "error",
        });
      }
    }
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Card
        elevation={4}
        sx={{
          backgroundColor: "white",
          color: "#0d2445",
          height: 80,
          width: 80,
          marginBottom: 1,
          textAlign: "center",
          ":hover": { boxShadow: 10, backgroundColor: "#ffffff" },
        }}
      >
        <CardActionArea
          sx={{ "& .MuiTouchRipple-ripple": { color: "blue" } }}
          onClick={addLine}
        >
          <TimelineIcon sx={{ marginTop: 2, marginBottom: -1 }} />
          <CardHeader
            title="Line"
            sx={{
              "& .MuiCardHeader-title": {
                color: "#000",
                fontSize: 12,
                fontFamily: "Roboto",
                fontWeight: 500,
                width: 60,
                marginLeft: -1,
              },
            }}
          />
        </CardActionArea>
      </Card>
      <TextField
        type="number"
        label="Stroke Width"
        value={lineStroke}
        onChange={handleStrokeWidthChange}
        inputProps={{ min: 1, "aria-label": "Line stroke width" }}
        InputProps={{
          endAdornment: <InputAdornment position="end">px</InputAdornment>,
        }}
        size="small"
        sx={{ width: 120 }}
      />
    </Stack>
  );
};

export default LineToolbar;
